import { AppForm, discountFields } from 'components/organisms';
import {
    AppFormItem,
    AppRadioButton,
    AppRadioGroup,
    AppTitle,
    AppTooltipQuestionMarkIcon,
} from 'components/atoms';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { formFields } from 'consts/form/formFields';
import { FormNavButtons } from 'components/molecules';
import { Flex, Form } from 'antd';
import { useEffect } from 'react';
import { useWatch } from 'antd/es/form/Form';
import { useNavigate, useParams } from 'react-router-dom';
import {
    DiscountFormFields,
    ChildId,
    DiscountType,
    DiscountTarget,
    DiscountApplyType,
} from 'types';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { commonTexts, settlementTexts } from 'consts/text';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { parseAddDiscountsFormFieldsToAddDiscountDTO } from 'utils/parsers/discounts/parseAddDiscountsFormFieldsToAddDiscountDTO';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { useChildrenDetailsPath } from 'hooks/useChildrenDetailsPath/useChildrenDetailsPath';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import { childDiscountsErrorParser } from '../../../../../utils/errorHandlers/child/childDiscountsErrorParser';

export const ChildAddDiscountPage = () => {
    const [form] = Form.useForm();
    const { displaySuccess, displayError } = useSimpleNotification();
    const { childId } = useParams<{ childId: ChildId }>();
    const navigate = useNavigate();
    const { getChildrenDetailsPath } = useChildrenDetailsPath();

    const isMobile = useIsMobile();

    const discountTarget = useWatch(formFields.discountTarget, form);
    const discountType = useWatch(formFields.discountType, form);
    const applyType = useWatch(formFields.applyType, form);
    const titleLevel = isMobile ? 4 : 3;

    useEffect(() => {
        if (applyType === DiscountApplyType.COMPONENT) {
            form.setFieldsValue({ [formFields.discountType]: DiscountType.PERCENTAGE_DISCOUNT });
        }
    }, [applyType, form]);

    const navigateToChildPage = () => {
        if (!childId) {
            return;
        }
        const path = getChildrenDetailsPath('', childId);
        navigate(path);
    };

    const handleAddDiscountSuccess = () => {
        displaySuccess(settlementTexts.discounts.general.addSuccess);
        navigateToChildPage();
    };

    const { data: extraLessonsData, isFetching: isFetchingExtraLessons } = useAppQuery(
        'EXTRA_LESSONS',
        [childId],
        () => StaffHTTPService.settlements.getExtraLessonsByChild(childId as ChildId),
        {
            enabled: discountTarget === DiscountTarget.EXTRA_LESSONS,
            refetchOnWindowFocus: false,
        },
    );

    const { mutate: addDiscount, isLoading: isAddDiscountLoading } = useAppMutation(
        StaffHTTPService.discounts.addDiscount,
        {
            key: ['ADD_DISCOUNT'],
            onSuccess: handleAddDiscountSuccess,
            onError: (error) => displayError(childDiscountsErrorParser(error)),
            invalidateQueryKeys: [['CHILD_DISCOUNTS', childId]],
        },
    );

    const onFinishForm = (data: DiscountFormFields) => {
        if (!childId) {
            return;
        }

        const parsedDiscount = parseAddDiscountsFormFieldsToAddDiscountDTO(childId, data);
        addDiscount(parsedDiscount);
    };

    const renderFieldsBasedOnType = () => {
        if (!discountTarget || !discountType || !applyType) {
            return undefined;
        }

        switch (discountType) {
            case DiscountType.VALUE_DISCOUNT:
                return discountFields(
                    discountTarget,
                    applyType,
                    discountType === DiscountType.VALUE_DISCOUNT,
                    extraLessonsData,
                    isFetchingExtraLessons,
                );
            case DiscountType.PERCENTAGE_DISCOUNT:
                return discountFields(
                    discountTarget,
                    applyType,
                    discountType === DiscountType.VALUE_DISCOUNT,
                    extraLessonsData,
                    isFetchingExtraLessons,
                );
            default:
                return undefined;
        }
    };

    return (
        <AppForm<DiscountFormFields>
            form={form}
            name="addDiscount"
            title={
                <Flex align="center" justify="center" gap={8}>
                    <AppTitle margin={0} level={titleLevel} textAlign="center">
                        {settlementTexts.discounts.addEditForm.addTitle}
                    </AppTitle>
                    <AppTooltipQuestionMarkIcon
                        color="#2051D5"
                        tooltip={settlementTexts.discounts.addEditForm.discountCalculationInfo}
                        overlayStyle={{ maxWidth: '560px' }}
                        overlayInnerStyle={{ textAlign: 'left', whiteSpace: 'pre-line' }}
                    />
                </Flex>
            }
            withGoBack
            maxWidth={600}
            onFinish={onFinishForm}
            isLoading={isAddDiscountLoading}
        >
            <AppFormItem
                label={settlementTexts.discounts.addEditForm.discountTarget}
                name={formFields.discountTarget}
            >
                <AppRadioGroup sameSizeOptionButtons={false} fullWidth>
                    <AppRadioButton value={DiscountTarget.MEAL}>
                        {settlementTexts.discounts.general.meals}
                    </AppRadioButton>
                    <AppRadioButton value={DiscountTarget.STAY}>
                        {settlementTexts.discounts.general.stay}
                    </AppRadioButton>
                    <AppRadioButton value={DiscountTarget.EXTRA_LESSONS}>
                        {settlementTexts.discounts.general.extraLessons}
                    </AppRadioButton>
                    <AppRadioButton value={DiscountTarget.ADDITIONAL_COST}>
                        {settlementTexts.discounts.general.additionalCost}
                    </AppRadioButton>
                    <AppRadioButton value={DiscountTarget.ALL_COSTS}>
                        {settlementTexts.discounts.general.allCosts}
                    </AppRadioButton>
                </AppRadioGroup>
            </AppFormItem>
            <AppFormItem
                label={settlementTexts.discounts.addEditForm.discountType}
                name={formFields.applyType}
            >
                <AppRadioGroup sameSizeOptionButtons={!isMobile}>
                    <AppRadioButton value={DiscountApplyType.COMPONENT}>
                        {settlementTexts.discounts.general.componentDiscount}
                    </AppRadioButton>
                    <AppRadioButton value={DiscountApplyType.CATEGORY}>
                        {settlementTexts.discounts.general.categoryDiscount}
                    </AppRadioButton>
                </AppRadioGroup>
            </AppFormItem>
            <AppFormItem
                label={settlementTexts.discounts.addEditForm.discountType}
                name={formFields.discountType}
            >
                <AppRadioGroup sameSizeOptionButtons={!isMobile}>
                    <AppRadioButton
                        value={DiscountType.VALUE_DISCOUNT}
                        disabled={applyType === DiscountApplyType.COMPONENT}
                    >
                        {settlementTexts.discounts.general.valueDiscount}
                    </AppRadioButton>
                    <AppRadioButton value={DiscountType.PERCENTAGE_DISCOUNT}>
                        {settlementTexts.discounts.general.percentageDiscount}
                    </AppRadioButton>
                </AppRadioGroup>
            </AppFormItem>
            {renderFieldsBasedOnType()}
            <FormNavButtons
                buttonText={commonTexts.actionLabels.save}
                leftIcon={<IconDeviceFloppy size={20} />}
            />
        </AppForm>
    );
};
